import React from 'react';
import { Code, Globe, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export default function AboutSection({
    t,
    portfolio,
    currentLang,
    getText,
    isAdmin,
    newSkill,
    setNewSkill,
    iconSearchTerm,
    setIconSearchTerm,
    suggestedIcons,
    selectedIcon,
    setSelectedIcon,
    handleCustomIconUpload,
    addSkill,
    deleteSkill,
    setShowAddSkill,
    showAddSkill
}) {
    // Map skill level to color/text
    const getLevelInfo = (level) => {
        if (level >= 90) return { label: 'Expert', color: 'bg-green-500' };
        if (level >= 75) return { label: 'Advanced', color: 'bg-blue-500' };
        if (level >= 50) return { label: 'Intermediate', color: 'bg-yellow-500' };
        return { label: 'Beginner', color: 'bg-slate-500' };
    };

    const getLanguageLevelPercentage = (level) => {
        const l = level.toLowerCase();
        if (l.includes('nativ')) return 100;
        if (l.includes('avan') || l.includes('advan') || l.includes('fluent')) return 90;
        if (l.includes('interm')) return 60;
        if (l.includes('basic') || l.includes('básic')) return 30;
        return 50; // default
    };

    return (
        <section id={t.nav[1].toLowerCase().replace(/\s+/g, '-')} style={{ paddingTop: '20px' }} className="pb-24 px-4 bg-slate-900">
            <div className="max-w-6xl mx-auto">
                <div className="animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">

                    <div className="grid md:grid-cols-2 gap-12 items-start">
                        {/* Bio Column - NOW INCLUDES TITLE LEFT ALIGNED */}
                        <div className="space-y-8 flex flex-col items-center md:items-start text-center md:text-left">
                            <h2 className="text-4xl md:text-5xl font-bold">
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                                    {t.about}
                                </span>
                            </h2>

                            <div className="bg-slate-800/30 backdrop-blur-sm rounded-3xl p-8 border border-slate-700/50 hover:border-cyan-500/30 transition-all duration-500 w-full">
                                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                                    {getText(portfolio.personal.bio, currentLang)}
                                </p>
                                <p className="text-lg text-slate-300 leading-relaxed">
                                    {getText(portfolio.personal.bio2, currentLang)}
                                </p>
                            </div>
                        </div>

                        {/* Skills and Languages Column */}
                        <div className="space-y-12">
                            {/* Skills Grid - Moved down visually as requested */}
                            <div className="mt-12 md:mt-48">
                                <div className="flex flex-col mb-8 pt-2 gap-4">
                                    <h3 className="text-3xl font-bold flex items-center gap-2 justify-center w-full">
                                        <Code className="text-cyan-400" />
                                        {t.skills}
                                    </h3>
                                    {isAdmin && (
                                        <button onClick={() => setShowAddSkill(true)} className="self-center bg-cyan-500/20 text-cyan-400 p-2 rounded-lg hover:bg-cyan-500 hover:text-white transition-all">
                                            <Plus size={20} />
                                        </button>
                                    )}
                                </div>

                                {/* Add Skill Form (Admin only) */}
                                {showAddSkill && (
                                    <div className="bg-slate-800 p-6 rounded-xl mb-6 border border-cyan-500/50 animate-fade-in-up">
                                        <div className="mb-4">
                                            <input type="text" placeholder={t.techName} value={newSkill.name} onChange={(e) => { setNewSkill({ ...newSkill, name: e.target.value }); setIconSearchTerm(e.target.value); }} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 mb-3 focus:ring-2 focus:ring-cyan-500 outline-none hover:border-cyan-500/50 transition-colors" />

                                            {/* Icon Suggestions */}
                                            {suggestedIcons.length > 0 && (
                                                <div className="mb-4">
                                                    <p className="text-xs text-slate-400 mb-2 font-medium ml-1">Selecciona un icono (o escribe para buscar):</p>
                                                    <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 flex gap-3 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
                                                        {suggestedIcons.map((icon, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => setSelectedIcon(icon)}
                                                                className={`p-3 rounded-lg hover:bg-slate-700 transition-all transform hover:scale-110 flex-shrink-0 ${selectedIcon === icon ? 'bg-cyan-500/20 border border-cyan-500 ring-2 ring-cyan-500/20' : 'border border-transparent'}`}
                                                                title={icon}
                                                            >
                                                                <img src={`https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${icon}/${icon}-original.svg`} alt={icon} className="w-8 h-8" onError={(e) => { e.target.onerror = null; e.target.src = `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${icon}/${icon}-plain.svg` }} />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Custom Icon Upload */}
                                            <div className="mb-4">
                                                <p className="text-xs text-slate-400 mb-2 font-medium ml-1">O sube tu propio icono:</p>
                                                <input type="file" accept="image/*" onChange={handleCustomIconUpload} className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/20" />
                                            </div>

                                            <input type="range" min="0" max="100" value={newSkill.level} onChange={(e) => setNewSkill({ ...newSkill, level: parseInt(e.target.value) })} className="w-full mb-3 accent-cyan-500" />
                                            <div className="flex gap-2">
                                                <button onClick={addSkill} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-2 rounded-lg transition-colors">{t.save}</button>
                                                <button onClick={() => setShowAddSkill(false)} className="bg-slate-700 hover:bg-slate-600 px-4 rounded-lg transition-colors">{t.cancel}</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                                    {portfolio.skills.map(skill => {
                                        const { color } = getLevelInfo(skill.level);
                                        return (
                                            <Card key={skill.id} className="bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 transition-all duration-300 group hover:-translate-y-1">
                                                <CardContent className="p-4 flex flex-col items-center justify-center gap-3 relative">
                                                    {isAdmin && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); deleteSkill(skill.id); }}
                                                            className="absolute top-2 right-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-700 rounded"
                                                            aria-label="Delete Skill"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                    <div className="w-12 h-12 flex items-center justify-center mb-1 transition-transform group-hover:scale-110 duration-300">
                                                        {skill.icon_url && skill.icon_url.startsWith('http') ? (
                                                            <img src={skill.icon_url} alt={skill.name} className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(34,211,238,0.3)]" />
                                                        ) : (
                                                            <img
                                                                src={`https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${skill.icon_name || skill.name.toLowerCase()}/${skill.icon_name || skill.name.toLowerCase()}-original.svg`}
                                                                alt={skill.name}
                                                                className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(34,211,238,0.3)]"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    // Fallback for names that might not match exactly or plain versions
                                                                    const name = skill.name.toLowerCase();
                                                                    if (!e.target.src.includes('plain')) {
                                                                        e.target.src = `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${name}/${name}-plain.svg`;
                                                                    } else {
                                                                        // Last resort fallback text
                                                                        e.target.style.display = 'none';
                                                                        e.target.parentNode.innerHTML = `<span class="text-2xl font-bold text-cyan-500">${skill.name[0]}</span>`;
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <span className="font-semibold text-slate-200 text-center">{skill.name}</span>
                                                    <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden mt-1">
                                                        <div
                                                            className={`h-full ${color} shadow-[0_0_10px_currentColor] transition-all duration-1000 ease-out`}
                                                            style={{ width: `${skill.level}%` }}
                                                        />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Languages Section - MOVED TO FULL WIDTH BOTTOM SECTION */}
                    <div className="mt-24 pt-12 border-t border-slate-800">
                        <h3 className="text-3xl font-bold flex items-center justify-center gap-3 mb-12 text-center">
                            <Globe className="text-cyan-400 w-8 h-8" />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                                {t.languages || 'Idiomas'}
                            </span>
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                            {portfolio.languages && portfolio.languages.map((lang, idx) => {
                                const percentage = getLanguageLevelPercentage(lang.level);
                                const getLanguageFlag = (langName) => {
                                    const l = langName.toLowerCase();
                                    if (l.includes('espa') || l.includes('span')) return '🇪🇸';
                                    if (l.includes('port')) return '🇵🇹';
                                    if (l.includes('ingl') || l.includes('engl')) return '🇺🇸';
                                    if (l.includes('fran')) return '🇫🇷';
                                    if (l.includes('ital')) return '🇮🇹';
                                    if (l.includes('germ') || l.includes('alem')) return '🇩🇪';
                                    return '🌍';
                                };
                                return (
                                    <div key={idx} className="group relative">
                                        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl opacity-20 group-hover:opacity-75 blur transition duration-500"></div>
                                        <div className="relative bg-slate-900 rounded-xl p-6 h-full border border-slate-800 hover:border-cyan-500/50 transition-all flex flex-col items-center text-center gap-4">
                                            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center font-bold text-3xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                                                {getLanguageFlag(lang.name)}
                                            </div>

                                            <h4 className="text-xl font-bold text-white">
                                                {(() => {
                                                    const langNameStart = lang.name.split(' ')[0].toLowerCase();
                                                    // Simple dictionary for common languages
                                                    const dict = {
                                                        'español': { es: 'Español', en: 'Spanish', pt: 'Espanhol' },
                                                        'spanish': { es: 'Español', en: 'Spanish', pt: 'Espanhol' },
                                                        'inglés': { es: 'Inglés', en: 'English', pt: 'Inglês' },
                                                        'ingles': { es: 'Inglés', en: 'English', pt: 'Inglês' },
                                                        'english': { es: 'Inglés', en: 'English', pt: 'Inglês' },
                                                        'portugués': { es: 'Portugués', en: 'Portuguese', pt: 'Português' },
                                                        'portugues': { es: 'Portugués', en: 'Portuguese', pt: 'Português' },
                                                        'portuguese': { es: 'Portugués', en: 'Portuguese', pt: 'Português' },
                                                        'francés': { es: 'Francés', en: 'French', pt: 'Francês' },
                                                        'french': { es: 'Francés', en: 'French', pt: 'Francês' },
                                                    };

                                                    // Try to match start of string to dictionary keys
                                                    const match = Object.keys(dict).find(k => langNameStart.includes(k) || k.includes(langNameStart));

                                                    if (match && dict[match][currentLang]) {
                                                        return dict[match][currentLang];
                                                    }
                                                    return lang.name;
                                                })()}
                                            </h4>

                                            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden mt-auto">
                                                <div
                                                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full shadow-[0_0_10px_rgba(6,182,212,0.5)] transition-all duration-1000 ease-out"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
